import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  postConcurrentSearchRequest,
  setConcurrentSearchLoading,
  resetConcurrentSearch,
} from '@client/model/concurrentSearchModel';
import { AppState } from '@client/model/store';

interface UseConcurrentSearchParams {
  requesterId?: string;
  aiPromptType?: string;
}

interface UseConcurrentSearchCallbacks {
  onSuccess?: (data: OpenAiCompletionDbRecord) => void;
  onError?: () => void;
}

interface UseConcurrentSearchResult {
  isSearching: boolean;
  startSearch: (
    params: UseConcurrentSearchParams,
    callbacks?: UseConcurrentSearchCallbacks,
  ) => void;
}

export function useConcurrentSearch(searchKey: string): UseConcurrentSearchResult {
  const dispatch = useDispatch();
  const searchState = useSelector((state: AppState) => state.concurrentSearch.searches[searchKey]);
  const [searchCallbacks, setSearchCallbacks] = useState<UseConcurrentSearchCallbacks | null>(
    null,
  );

  const startSearch = (
    params: UseConcurrentSearchParams,
    callbacks?: UseConcurrentSearchCallbacks,
  ) => {
    if (!params.requesterId || !params.aiPromptType) {
      return;
    }

    dispatch(resetConcurrentSearch(searchKey));
    dispatch(setConcurrentSearchLoading({ searchKey, isLoading: true }));
    setSearchCallbacks(callbacks || null);

    dispatch(postConcurrentSearchRequest({ 
      ...params, 
      searchKey 
    }));
  };

  useEffect(() => {
    if (!searchState || searchState.isLoading || !searchCallbacks) return;

    // Check for error first
    if (searchState.error) {
      searchCallbacks.onError?.();
      setSearchCallbacks(null);
      return;
    }

    // Search found existing data
    if (searchState.data && !!searchState.data.results) {
      searchCallbacks.onSuccess?.(searchState.data);
      setSearchCallbacks(null);
      return;
    } 
    
    // Search returned data but without results
    if (searchState.data) {
      searchCallbacks.onError?.();
      setSearchCallbacks(null);
      return;
    }
  }, [searchState?.data, searchState?.error, searchState?.isLoading, searchCallbacks]);

  return {
    isSearching: searchState?.isLoading || false,
    startSearch,
  };
}